import { useState } from 'react';
import { useAuth } from './useAuth';
import { getApiHeaders, getApiHeadersWithoutContentType, API_TYPES } from '../utils/apiHeaders';
import { validateReportCount, validateReportSize } from '../utils/billingHelpers';

const DB_API_BASE_URL = import.meta.env.VITE_DB_API_URL || '/api'; // DB Function
const PBI_API_BASE_URL = import.meta.env.VITE_PBI_API_URL || 'http://localhost:7056/api'; // PBI API Function

export const useReportManagement = (assignedWorkspace, onSuccess) => {
const [selectedFile, setSelectedFile] = useState(null);
const [loading, setLoading] = useState(false);
const [deleteProgress, setDeleteProgress] = useState(null);
const [uploadProgress, setUploadProgress] = useState(null);
const { getAccessToken, getUserEmail } = useAuth();

  // Get current admin ID for DB operations
  const getCurrentAdminId = (subscriptionData) => {
    const currentUserEmail = getUserEmail();
    const currentAdmin = subscriptionData?.admins?.find(admin => 
      admin.email === currentUserEmail || admin.Email === currentUserEmail
    );
    return currentAdmin?.adminID || currentAdmin?.AdminID;
  };

  // Helper function to verify report exists in Power BI
  const verifyReportInPowerBI = async (workspaceId, reportId, token, maxRetries = 3, delayMs = 2000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const verifyResponse = await fetch(`${PBI_API_BASE_URL}/reports/${workspaceId}`, {
          method: 'GET',
          headers: getApiHeaders(token, API_TYPES.PBI)
        });

        if (verifyResponse.ok) {
          const reports = await verifyResponse.json();
          const reportExists = reports.some(r => 
            (r.id || r.reportId || r.ReportID) === reportId
          );
          
          if (reportExists) {
            return true;
          }
          
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }
        } else {
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }
        }
      } catch (error) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
    
    return false;
  };

  // Helper function to cleanup Power BI report
  const cleanupPowerBIReport = async (workspaceId, reportId, token) => {
    try {
      const cleanupResponse = await fetch(`${PBI_API_BASE_URL}/reports/${workspaceId}/${reportId}`, {
        method: 'DELETE',
        headers: getApiHeaders(token, API_TYPES.PBI)
      });

      if (cleanupResponse.ok) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  const uploadReport = async (subscriptionData, overwrite = false) => {
    if (!assignedWorkspace || !selectedFile || !subscriptionData) {
      throw new Error('Missing workspace, file, or subscription data');
    }

    setLoading(true);
    setUploadProgress({
      powerbi: 'pending',
      database: 'pending',
      complete: 'pending'
    });
    let reportId = null;
    let uploadedToPBI = false;
    let storedInDB = false;
    let existingReport = null;

    try {
      const token = await getAccessToken();
      const adminId = getCurrentAdminId(subscriptionData);
      
      if (!adminId) {
        throw new Error('Unable to determine current admin ID for database operations');
      }

      // Check if a report with the same name already exists
      const reportName = selectedFile.name.replace(/\.pbix$/i, '');
      existingReport = assignedWorkspace.reports?.find(r => 
        (r.ReportName || r.reportName) === reportName
      );

      if (existingReport && !overwrite) {
        // Report exists and overwrite not confirmed - throw error to show confirmation dialog
        const duplicateError = new Error('DUPLICATE_REPORT');
        duplicateError.reportName = reportName;
        throw duplicateError;
      }

      // Validate report count limit (only for new reports, not overwrites)
      if (!existingReport) {
        const countValidation = validateReportCount(subscriptionData);
        if (!countValidation.isValid) {
          throw new Error(countValidation.errorMessage);
        }
      }

      // Validate report size limit
      const sizeValidation = validateReportSize(selectedFile.size, subscriptionData);
      if (!sizeValidation.isValid) {
        throw new Error(sizeValidation.errorMessage);
      }

      const fileSize = selectedFile.size;
      const fileSizeMB = (fileSize / 1024 / 1024).toFixed(2);

      // Step 1: Upload to Power BI using PBI API Function
      
      setUploadProgress({
        powerbi: 'in-progress',
        database: 'pending',
        complete: 'pending'
      });
      
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Dynamic timeout based on file size (minimum 5 minutes, +1 minute per MB)
      const timeoutMs = Math.max(5 * 60 * 1000, (fileSize / 1024 / 1024) * 60 * 1000 + 3 * 60 * 1000);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      let pbiResponse;
      try {
        // Add workspaceId, servicePrincipalProfileId, and overwrite parameter to the URL
        const uploadUrl = `${PBI_API_BASE_URL}/ImportReport?workspaceId=${assignedWorkspace.workspaceID}&servicePrincipalProfileId=${assignedWorkspace.servicePrincipalProfileID}${overwrite ? '&nameConflict=Overwrite' : ''}`;
        
        pbiResponse = await fetch(uploadUrl, {
          method: 'POST',
          headers: getApiHeadersWithoutContentType(token, API_TYPES.PBI),
          body: formData,
          signal: controller.signal
        });
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          setUploadProgress({
            powerbi: 'error',
            database: 'pending',
            complete: 'pending'
          });
          const timeoutMinutes = Math.ceil(timeoutMs / 60000);
          throw new Error(`Upload timed out after ${timeoutMinutes} minutes. Large reports may require more processing time. Please try again or contact support if the issue persists.`);
        }
        setUploadProgress({
          powerbi: 'error',
          database: 'pending',
          complete: 'pending'
        });
        throw error;
      }

      clearTimeout(timeoutId);

      if (!pbiResponse.ok) {
        const errorText = await pbiResponse.text();
        
        // Check if this is a duplicate error
        if (pbiResponse.status === 500 && errorText.includes('DuplicatePackageNotFoundError')) {
          const duplicateError = new Error('DUPLICATE_REPORT');
          duplicateError.reportName = selectedFile.name.replace(/\.pbix$/i, '');
          throw duplicateError;
        }
        
        setUploadProgress({
          powerbi: 'error',
          database: 'pending',
          complete: 'pending'
        });
        throw new Error(`Power BI upload failed (${pbiResponse.status}): ${errorText}`);
      }

      // Handle response format dynamically
      const contentType = pbiResponse.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        const pbiResult = await pbiResponse.json();
        
        if (!pbiResult.reports || pbiResult.reports.length === 0) {
          throw new Error(`No reports found in Power BI response. Import may have failed.`);
        }
        
        reportId = pbiResult.reports[0].id;
        
      } else {
        const pbiResult = await pbiResponse.text();
        
        const reportIdMatch = pbiResult.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
        
        if (!reportIdMatch) {
          throw new Error(`Could not extract report ID from Power BI response: ${pbiResult}`);
        }
        
        reportId = reportIdMatch[1];
      }

      uploadedToPBI = true;
      
      setUploadProgress({
        powerbi: 'completed',
        database: 'in-progress',
        complete: 'pending'
      });

      // Validate GUID format
      const guidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
      if (!guidPattern.test(reportId)) {
        throw new Error(`Invalid report ID format: ${reportId}`);
      }

      // Step 2: Store or Update report metadata in DB
      
      let dbResponse;
      let reportPayload;

      if (existingReport) {
        // Overwrite: Use UpdateReport endpoint
        reportPayload = {
          reportID: reportId,
          uploadedByAdminID: adminId
        };
        
        dbResponse = await fetch(`${DB_API_BASE_URL}/UpdateReport?adminId=${adminId}`, {
          method: 'PUT',
          headers: getApiHeaders(token, API_TYPES.DATABASE),
          body: JSON.stringify(reportPayload)
        });
      } else {
        // New report: Use CreateReport endpoint
        reportPayload = {
          reportID: reportId,
          workspaceID: assignedWorkspace.workspaceID,
          reportName: selectedFile.name.replace(/\.pbix$/i, ''),
          uploadedByAdminID: adminId
        };
        
        dbResponse = await fetch(`${DB_API_BASE_URL}/CreateReport?adminId=${adminId}`, {
          method: 'POST',
          headers: getApiHeaders(token, API_TYPES.DATABASE),
          body: JSON.stringify(reportPayload)
        });
      }

      if (!dbResponse.ok) {
        const dbErrorText = await dbResponse.text();
        
        setUploadProgress({
          powerbi: 'completed',
          database: 'error',
          complete: 'pending'
        });
        
        // Cleanup: Remove from Power BI since database failed (only if not overwriting existing)
        if (reportId && !existingReport) {
          const cleanupSuccess = await cleanupPowerBIReport(
            assignedWorkspace.workspaceID, 
            reportId, 
            token
          );
        }

        throw new Error(`Database ${existingReport ? 'update' : 'storage'} failed (${dbResponse.status}): ${dbErrorText}${!existingReport ? '. Report has been removed from Power BI.' : ''}`);
      }

      const dbResult = await dbResponse.json();
      storedInDB = true;
      
      setUploadProgress({
        powerbi: 'completed',
        database: 'completed',
        complete: 'in-progress'
      });

      setLoading(false);
      
      setUploadProgress({
        powerbi: 'completed',
        database: 'completed',
        complete: 'completed'
      });

      // Small delay to show completion state
      await new Promise(resolve => setTimeout(resolve, 500));

      // Clear the selected file and trigger success callback
      setSelectedFile(null);
      onSuccess?.();
      
      // Return both the message and the report ID for the assignment popup
      return {
        message: `Report "${selectedFile.name}" ${existingReport ? 'overwritten' : 'uploaded'} successfully`,
        reportId: reportId,
        reportName: selectedFile.name.replace(/\.pbix$/i, ''),
        isOverwrite: !!existingReport
      };
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
      // Clear progress after a delay
      setTimeout(() => setUploadProgress(null), 1000);
    }
  };

  const deleteReport = async (reportId, subscriptionData, deleteDataset = true) => {
    if (!assignedWorkspace || !subscriptionData) {
      throw new Error('Missing workspace or subscription data');
    }

    if (!reportId) {
      throw new Error('Missing report ID for deletion');
    }

    setLoading(true);
    setDeleteProgress({
      database: 'pending',
      powerbi: 'pending',
      complete: 'pending'
    });
    let removedFromDB = false;
    let removedFromPBI = false;

    try {
      const token = await getAccessToken();
      const adminId = getCurrentAdminId(subscriptionData);
      
      if (!adminId) {
        throw new Error('Unable to determine current admin ID for database operations');
      }

      // Step 1: Remove from database first (to prevent user access)
      setDeleteProgress({
        database: 'in-progress',
        powerbi: 'pending',
        complete: 'pending'
      });
      
      const deletePayload = {
        reportID: reportId,
        workspaceID: assignedWorkspace.workspaceID,
        adminEmail: getUserEmail()
      };

      const dbResponse = await fetch(`${DB_API_BASE_URL}/DeleteReport?adminId=${adminId}`, {
        method: 'DELETE',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify(deletePayload)
      });

      if (!dbResponse.ok) {
        const dbErrorText = await dbResponse.text();
        setDeleteProgress({
          database: 'error',
          powerbi: 'pending',
          complete: 'pending'
        });
        throw new Error(`Database deletion failed (${dbResponse.status}): ${dbErrorText}`);
      }

      const dbResult = await dbResponse.json();
      removedFromDB = true;
      setDeleteProgress({
        database: 'completed',
        powerbi: 'in-progress',
        complete: 'pending'
      });

      // Step 2: Delete from Power BI (with optional dataset deletion)
      
      // Build URL with workspaceId, reportId, servicePrincipalProfileId, and deleteDataset query parameters
      const deleteUrl = `${PBI_API_BASE_URL}/reports/${assignedWorkspace.workspaceID}/${reportId}?servicePrincipalProfileId=${assignedWorkspace.servicePrincipalProfileID}${deleteDataset ? '&deleteDataset=true' : ''}`;
      
      const pbiResponse = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: getApiHeaders(token, API_TYPES.PBI)
      });

      if (!pbiResponse.ok) {
        const pbiErrorText = await pbiResponse.text();
        
        setDeleteProgress({
          database: 'completed',
          powerbi: 'error',
          complete: 'pending'
        });
        
        // Important: Don't restore database record since the user intended to delete
        // This ensures the report is inaccessible via the app even if PBI deletion failed
        
        // Note the partial failure but still consider the operation successful from user perspective
        onSuccess?.();
        return `Report deleted from application (database). Warning: Report may still exist in Power BI service and will need manual cleanup.`;
      }

      removedFromPBI = true;
      setDeleteProgress({
        database: 'completed',
        powerbi: 'completed',
        complete: 'in-progress'
      });

      setDeleteProgress({
        database: 'completed',
        powerbi: 'completed',
        complete: 'completed'
      });

      // Small delay to show completion state
      await new Promise(resolve => setTimeout(resolve, 500));

      onSuccess?.();
      
      const datasetMessage = deleteDataset ? ' and associated dataset' : '';
      return `Report${datasetMessage} deleted successfully from both database and Power BI. ${dbResult.removedAssignments || 0} user assignments were removed.`;

    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
      // Clear progress after a delay
      setTimeout(() => setDeleteProgress(null), 1000);
    }
  };

  // Helper function to get report status from both systems
  const getReportStatus = async (reportId, subscriptionData) => {

    try {
      const token = await getAccessToken();
      const adminId = getCurrentAdminId(subscriptionData);

      // Check database
      let dbExists = false;
      try {
        const dbResponse = await fetch(`${DB_API_BASE_URL}/GetSubscriptionByAdmin?adminEmail=${getUserEmail()}`, {
          method: 'GET',
          headers: getApiHeaders(token, API_TYPES.DATABASE)
        });
        
        if (dbResponse.ok) {
          const data = await dbResponse.json();
          dbExists = data.workspaces?.some(w => 
            w.reports?.some(r => r.reportID === reportId)
          );
        }
      } catch (e) {
        // Database check failed
      }

      // Check Power BI
      let pbiExists = false;
      try {
        const pbiResponse = await fetch(`${PBI_API_BASE_URL}/reports/${assignedWorkspace.workspaceID}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (pbiResponse.ok) {
          const reports = await pbiResponse.json();
          pbiExists = reports.some(r => 
            (r.id || r.reportId || r.ReportID) === reportId
          );
        }
      } catch (e) {
        // Power BI check failed
      }

      return { dbExists, pbiExists };
    } catch (error) {
      return { dbExists: null, pbiExists: null };
    }
  };


  return {
    selectedFile,
    setSelectedFile,
    loading,
    deleteProgress,
    uploadProgress,
    uploadReport,
    deleteReport,
    getReportStatus
  };
};
